import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, CheckCircle2, FileText, FolderOpen, Send } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { DocumentDistributionPanel } from '../DocumentDistributionPanel';
import { GovernanceFilingPanel } from '../GovernanceFilingPanel';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface RecordJournalPhase2Props {
  task: any;
  phaseData: any;
  onPhaseComplete: () => void;
}

export function RecordJournalPhase2({ task, phaseData, onPhaseComplete }: RecordJournalPhase2Props) {
  const { toast } = useToast();
  const [documents, setDocuments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [distributionComplete, setDistributionComplete] = useState(false);
  const [filingComplete, setFilingComplete] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);

  const workflowData = task.workflow_data || {};
  const recordType = workflowData.record_type || '';
  const isResolution = recordType === 'Resolution by Written Consent';

  useEffect(() => {
    loadDocuments();
    checkCompletionStatus();
  }, [task.id]);

  useEffect(() => {
    if (documents.length > 0) {
      checkDistributionStatus();
      checkFilingStatus();
    }
  }, [documents]);

  async function loadDocuments() {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('generated_documents')
        .select('*')
        .eq('task_id', task.id)
        .order('generated_at', { ascending: true });

      if (error) throw error;
      setDocuments(data || []);
    } catch (error) {
      console.error('Error loading documents:', error);
    } finally {
      setLoading(false);
    }
  }

  async function checkCompletionStatus() {
    if (task.status === 'completed') {
      setIsCompleted(true);
    }
  }

  async function checkDistributionStatus() {
    const { data } = await supabase
      .from('document_distribution_log')
      .select('id')
      .eq('task_id', task.id)
      .limit(1);

    setDistributionComplete(data && data.length > 0);
  }

  async function checkFilingStatus() {
    const { data } = await supabase
      .from('governance_documents')
      .select('id')
      .eq('generated_from_task_id', task.id)
      .limit(1);

    setFilingComplete(data && data.length > 0);
  }

  async function handleCompleteWorkflow() {
    setIsCompleting(true);
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No active session');

      // Call the complete-workflow-phase edge function
      const response = await supabase.functions.invoke('complete-workflow-phase', {
        body: {
          taskId: task.id,
          phaseNumber: 2,
          workflowKey: 'record_journal_transaction'
        }
      });

      if (response.error) {
        throw new Error(response.error.message || 'Failed to complete workflow');
      }

      // Update task status
      await supabase
        .from('custom_tasks')
        .update({
          status: 'completed',
          completed_at: new Date().toISOString(),
          workflow_completion_percentage: 100
        })
        .eq('id', task.id);

      // Update phase status
      await supabase
        .from('task_phases')
        .update({ status: 'completed' })
        .eq('task_id', task.id)
        .eq('phase_number', 2);

      toast({
        title: "Workflow Completed",
        description: "Record has been filed successfully.",
      });

      setIsCompleted(true);
      onPhaseComplete();
    } catch (error: any) {
      console.error('Error completing workflow:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to complete workflow",
        variant: "destructive",
      });
    } finally {
      setIsCompleting(false);
    }
  }

  // Get documents that should be filed (exclude Resolution documents for Resolutions section)
  function getDocumentsForFiling() {
    if (isResolution) {
      // Resolution documents go to Resolutions & Actions section, not Document Repository
      return documents.filter(d => !d.document_type?.includes('resolution'));
    }
    return documents;
  }

  // Get additional recipients based on record type
  function getAdditionalRecipients() {
    const recipients: any[] = [];
    
    if (recordType === 'Private Contribution Received' && workflowData.contributor_name) {
      // Could add contributor if they have an email
    }
    
    if (recordType === 'Grant or Distribution Made' && workflowData.recipient_name) {
      // Could add recipient if they have an email
    }
    
    return recipients;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (isCompleted) {
    return (
      <Card className="border-green-500 bg-green-50 dark:bg-green-950/20">
        <CardContent className="p-6">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="h-8 w-8 text-green-500" />
            <div>
              <h3 className="text-lg font-semibold text-green-700 dark:text-green-400">
                Record Filed Successfully
              </h3>
              <p className="text-green-600 dark:text-green-500">
                The {recordType.toLowerCase()} has been filed to UNA records.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const documentsForFiling = getDocumentsForFiling();
  const canComplete = documents.length > 0;

  return (
    <div className="space-y-6">
      {/* Document Distribution */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Send className="h-5 w-5" />
            Distribute Documents
          </CardTitle>
          <CardDescription>
            Send the record document to relevant parties via email.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {documents.length > 0 ? (
            <DocumentDistributionPanel
              taskId={task.id}
              unaId={task.una_id}
              documents={documents.map(d => ({
                id: d.id,
                document_name: d.document_name,
                file_url: d.signed_file_url || d.file_url,
                signed_file_url: d.signed_file_url,
                status: d.status,
                generated_at: d.generated_at,
                template_key: d.document_type,
                task_id: d.task_id,
                una_id: d.una_id
              }))}
              additionalRecipients={getAdditionalRecipients()}
              onDistributionComplete={() => {
                setDistributionComplete(true);
                toast({
                  title: "Distribution Complete",
                  description: "Documents have been sent to recipients.",
                });
              }}
            />
          ) : (
            <p className="text-muted-foreground">No documents available for distribution.</p>
          )}
          
          {distributionComplete && (
            <div className="mt-4 flex items-center gap-2 text-green-600">
              <CheckCircle2 className="h-4 w-4" />
              <span>Distribution complete</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Governance Filing */}
      {documentsForFiling.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <FolderOpen className="h-5 w-5" />
              File to UNA Records
            </CardTitle>
            <CardDescription>
              {isResolution 
                ? 'Resolution will be filed to the Resolutions & Actions section.'
                : 'File this record to the UNA Document Repository.'
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            <GovernanceFilingPanel
              taskId={task.id}
              unaId={task.una_id}
              documents={documentsForFiling.map(d => ({
                id: d.id,
                document_name: d.document_name,
                file_url: d.signed_file_url || d.file_url,
                signed_file_url: d.signed_file_url,
                status: d.status,
                generated_at: d.generated_at,
                template_key: d.document_type,
                task_id: d.task_id,
                una_id: d.una_id
              }))}
              onFilingComplete={() => {
                setFilingComplete(true);
                toast({
                  title: "Filing Complete",
                  description: "Documents have been filed to governance records.",
                });
              }}
            />
            
            {filingComplete && (
              <div className="mt-4 flex items-center gap-2 text-green-600">
                <CheckCircle2 className="h-4 w-4" />
                <span>Filing complete</span>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Workflow Completion */}
      <Alert className={canComplete ? '' : 'border-muted'}>
        <AlertDescription className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {canComplete ? (
              <FileText className="h-5 w-5 text-primary" />
            ) : (
              <FileText className="h-5 w-5 text-muted-foreground" />
            )}
            <span className={!canComplete ? 'text-muted-foreground' : ''}>
              {canComplete 
                ? 'Ready to complete the workflow and finalize the record.'
                : 'Generate a document in Phase 1 to complete this workflow.'
              }
            </span>
          </div>
          <Button 
            onClick={handleCompleteWorkflow} 
            disabled={!canComplete || isCompleting}
          >
            {isCompleting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Completing...
              </>
            ) : (
              <>
                <CheckCircle2 className="mr-2 h-4 w-4" />
                Complete Workflow
              </>
            )}
          </Button>
        </AlertDescription>
      </Alert>
    </div>
  );
}
